// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Themed';
import { getGameDisplayName } from '@/features/game-definitions/i18n';
import appColors from '@/theme/colors';
import SectionHeading from '../shared/SectionHeading';
import type { GameDefinition } from '../../types';
import { normalizeGameId } from '../../utils';

type Props = {
  roundId: string;
  enabledGameDefinitions: GameDefinition[];
  roundGameIds: string[];
  tournamentGameIds: string[];
  onUpdateRoundGames: (roundId: string, gameIds: string[]) => void;
};

export default function RoundGamesEditor({
  roundId,
  enabledGameDefinitions,
  roundGameIds,
  tournamentGameIds,
  onUpdateRoundGames,
}: Props) {
  const roundGameSet = new Set(roundGameIds.map(normalizeGameId));
  const tournamentGameSet = new Set(tournamentGameIds.map(normalizeGameId));

  const handleToggle = useCallback(
    (gameId: string) => {
      const normalizedId = normalizeGameId(gameId);
      // Can't toggle tour-level games
      if (tournamentGameSet.has(normalizedId)) return;

      const currentRoundOnly = roundGameIds.filter((id) => !tournamentGameSet.has(normalizeGameId(id)));
      if (roundGameSet.has(normalizedId)) {
        // Remove from round
        onUpdateRoundGames(roundId, currentRoundOnly.filter((id) => normalizeGameId(id) !== normalizedId));
      } else {
        // Add to round
        onUpdateRoundGames(roundId, [...currentRoundOnly, gameId]);
      }
    },
    [roundId, roundGameIds, roundGameSet, tournamentGameSet, onUpdateRoundGames]
  );

  const roundOnlyCount = roundGameIds.filter((id) => !tournamentGameSet.has(normalizeGameId(id))).length;

  return (
    <View style={styles.container}>
      <SectionHeading title="Round Games" count={roundGameIds.length} />
      <Text style={styles.hint}>
        Tour games are auto-included and cannot be removed. Additional games can be toggled.
      </Text>
      <View style={styles.list}>
        {enabledGameDefinitions.map((game) => {
          const normalized = normalizeGameId(game.id);
          const isTourGame = tournamentGameSet.has(normalized);
          const isInRound = roundGameSet.has(normalized);
          const isChecked = isTourGame || isInRound;

          return (
            <Pressable
              key={game.id}
              style={[
                styles.gameRow,
                isChecked && styles.gameRowChecked,
                isTourGame && styles.gameRowTour,
              ]}
              onPress={() => handleToggle(game.id)}
              disabled={isTourGame}
            >
              <View style={[
                styles.checkbox,
                isChecked && styles.checkboxChecked,
                isTourGame && styles.checkboxTour,
              ]}>
                {isChecked && <Text style={styles.checkmark}>✓</Text>}
              </View>
              <Text style={[
                styles.gameName,
                isChecked && styles.gameNameChecked,
                isTourGame && styles.gameNameTour,
              ]}>
                {getGameDisplayName(game)}
              </Text>
              {isTourGame && (
                <View style={styles.tourBadge}>
                  <Text style={styles.tourBadgeText}>Tour</Text>
                </View>
              )}
            </Pressable>
          );
        })}
        {enabledGameDefinitions.length === 0 && (
          <Text style={styles.emptyText}>No games available</Text>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 8,
  },
  hint: {
    fontSize: 12,
    color: appColors.text.muted,
    fontStyle: 'italic',
    paddingHorizontal: 4,
  },
  list: {
    gap: 4,
  },
  gameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  gameRowChecked: {
    backgroundColor: appColors.accent.subtleBg,
    borderColor: appColors.accent.subtleBorder,
  },
  gameRowTour: {
    opacity: 0.7,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: appColors.border.strong,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: appColors.accent.primary,
    borderColor: appColors.accent.primary,
  },
  checkboxTour: {
    backgroundColor: appColors.surface.muted,
    borderColor: appColors.surface.muted,
  },
  checkmark: {
    fontSize: 14,
    fontWeight: '800',
    color: '#fff',
    lineHeight: 16,
  },
  gameName: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: appColors.text.secondary,
  },
  gameNameChecked: {
    color: appColors.text.primary,
    fontWeight: '700',
  },
  gameNameTour: {
    color: appColors.text.muted,
  },
  tourBadge: {
    backgroundColor: appColors.accent.subtleBg,
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 1,
  },
  tourBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: appColors.accent.primary,
  },
  emptyText: {
    fontSize: 14,
    color: appColors.text.muted,
    fontStyle: 'italic',
    paddingVertical: 12,
  },
});
